import React, { useState, useEffect, useContext } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useHistory } from "react-router-dom";
import axios from "axios";
import { Helmet } from "react-helmet";

// Contexts & Variables
import { AuditContext } from "../../../../contexts/Audit";
import { BASE_URL } from "../../../../BaseUrl";
import { getCookie, _t, customStyle } from "../../../../functions/Functions";

// Components
import AuditSidebar from "./AuditSidebar";
import CustomSpinLoader from "../../../components/CustomSpinLoader";
import { ToastSuccess, ToastError } from "../../../../functions/ToastShow";
import Select from "react-select";

const RcrQuestionAction = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const history = useHistory();
  const { rcrSections, getRcrSections, loading } = useContext(AuditContext);

  const [fetchingData, setFetchingData] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Form State
  const [formData, setFormData] = useState({
    section: null,
    question_text: "",
    s_no: "",
    max_marks: "",
    sort_order: "",
    is_critical: 1,
    is_active: 1,
  });

  useEffect(() => {
    if (!rcrSections) getRcrSections();

    if (id) {
      fetchQuestionDetails(id);
    }
  }, [id]);

  const fetchQuestionDetails = async (questionId) => {
    setFetchingData(true);
    try {
      const url = BASE_URL + `/settings/admin-questions/` + questionId;
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${getCookie()}` },
      });
      
      if (res.data && res.data.status) {
        const data = res.data.data;
        setFormData({
          section: data.section || null,
          question_text: data.question_text || data.title || data.question || "",
          s_no: data.s_no || "",
          max_marks: data.max_marks || data.weight || "",
          sort_order: data.sort_order || data.s_no || "",
          is_critical: data?.is_critical ? 1 : 0,
          is_active: data?.is_active ? 1 : 0,
        });
      } else {
        ToastError({ title: res.data?.message || _t(t("Question not found.")) });
      }
      setFetchingData(false);
    } catch (error) {
      setFetchingData(false);
      ToastError({ title: _t(t("Failed to load question details")) });
    }
  };

  const handleSelectChange = (name, selectedOption) => {
    setFormData(prev => ({ 
      ...prev, 
      [name]: selectedOption || null
    }));
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ 
      ...prev, 
      [name]: type === "checkbox" ? (checked ? 1 : 0) : value 
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.section || !formData.question_text || !formData.max_marks) {
      ToastError({ title: _t(t("Please fill all required fields")) });
      return;
    }

    setSubmitting(true);
    
    // Construct payload
    const payload = {
      section_id: Number(formData.section?.id),
      question_text: formData.question_text,
      max_marks: Number(formData.max_marks),
      is_critical: formData.is_critical,
      is_active: formData.is_active
    };
    
    if (formData.s_no) {
      payload.s_no = Number(formData.s_no);
    }
    if (formData.sort_order) {
      payload.sort_order = Number(formData.sort_order);
    }

    if (id) {
      payload.question_id = id;
    }

    try {
      let endpoint = "/settings/admin-questions";
      if(id){
        endpoint += `/${id}`;
      }
      const method = id ? "put" : "post"
      const url = `${BASE_URL}${endpoint}`;
      
      const res = await axios[method](url, payload, {
        headers: { Authorization: `Bearer ${getCookie()}` },
      });
      
      if (res.data && res.data.status === false) {
        let errorMsg = res.data.message || _t(t("Failed to save question"));
        if (res.data.errors) {
          errorMsg = Object.values(res.data.errors).flat().join(" ");
        }
        ToastError({ title: errorMsg });
        setSubmitting(false);
        return;
      }
      
      ToastSuccess({ title: _t(t(`Question successfully ${id ? 'updated' : 'created'}`)) });
      history.push("/dashboard/audit/rcr-question-list");
    } catch (error) {
      const resp = error.response?.data;
      if (resp && resp.status === false) {
        let errorMsg = resp.message || _t(t("Failed to save question"));
        if (resp.errors) {
          errorMsg = Object.values(resp.errors).flat().join(" ");
        }
        ToastError({ title: errorMsg });
      } else {
        ToastError({ title: _t(t("Failed to save question")) });
      }
      setSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>{id ? _t(t("Edit Question")) : _t(t("Add Question"))}</title>
      </Helmet>

      <main id="main" data-simplebar>
        <div className="container-fluid">
          <div className="row t-mt-10 gx-2">
            <div className="col-lg-2 col-xxl-2 t-mb-30 mb-lg-0">
              <AuditSidebar />
            </div>
            
            <div className="col-lg-10 col-xxl-10 t-mb-30 mb-lg-0">
              <div className="t-bg-white">
                <div className="fk-scroll--pos-menu table-bottom-info-hide" data-simplebar>
                  <div className="t-pl-15 t-pr-15">
                    <div className="row gx-2 align-items-center t-pt-15 t-pb-15">
                      <div className="col-md-6 col-lg-5 t-mb-15 mb-md-0">
                        <ul className="t-list fk-breadcrumb">
                          <li className="fk-breadcrumb__list">
                            <span className="t-link fk-breadcrumb__link text-capitalize">
                              {id ? _t(t("Edit Question")) : _t(t("New Question"))}
                            </span>
                          </li>
                        </ul>
                      </div>
                    </div>
                    
                    <>
                    {(loading || fetchingData) ? (
                      <div className="d-flex justify-content-center py-5">
                        <CustomSpinLoader loading={loading || fetchingData}/>
                      </div>
                    ) : (
                    <form onSubmit={handleSubmit}>
                      <div className="border border-secondary-light rounded p-3 my-2 bg-white">
                        <div className="row mb-3">
                        <div className="col-md-6">
                          <div className="form-group mb-3">
                            <label className="font-weight-600 text-dark form-label">
                              {_t(t("Section"))} <span className="text-danger">*</span>
                            </label>
                            <Select
                              options={rcrSections || []}
                              getOptionLabel={(option) =>
                                option.name
                              }
                              getOptionValue={(option) =>
                                option.id
                              }
                              value={formData.section}
                              onChange={(selected) => handleSelectChange('section', selected)}
                              placeholder={_t(t("Select Section..."))}
                              classNamePrefix="select table-text"
                              styles={customStyle}
                            />
                          </div>
                        </div>
                        
                        <div className="col-md-6">
                          <div className="form-group mb-3">
                            <label className="font-weight-600 text-dark form-label">
                              {_t(t("Max Marks"))} <span className="text-danger">*</span>
                            </label>
                            <input
                              type="number"
                              name="max_marks"
                              className="form-control border-secondary-light"
                              value={formData.max_marks}
                              onChange={handleInputChange}
                              min="0"
                              placeholder="e.g. 5"
                              required
                            />
                          </div>
                        </div>
                      </div>

                      <div className="row mb-3">
                        <div className="col-md-12">
                          <div className="form-group mb-3">
                            <label className="font-weight-600 text-dark form-label">
                              {_t(t("Question Text"))} <span className="text-danger">*</span>
                            </label>
                            <textarea
                              name="question_text"
                              className="form-control border-secondary-light"
                              rows="3"
                              value={formData.question_text}
                              onChange={handleInputChange}
                              placeholder={_t(t("e.g. Are walk-in chiller temperatures maintained between 1°C and 4°C?"))}
                              required
                              style={{ resize: "vertical" }}
                            ></textarea>
                          </div>
                        </div>
                      </div>

                      <div className="row mb-3">
                        <div className="col-md-6">
                          <div className="form-group mb-3">
                            <label className="font-weight-600 text-dark form-label">
                              {_t(t("Serial No. (S/N)"))}
                            </label>
                            <input
                              type="number"
                              name="s_no"
                              className="form-control border-secondary-light"
                              value={formData.s_no}
                              onChange={handleInputChange}
                              placeholder="e.g. 16"
                            />
                          </div>
                        </div>
                        
                        <div className="col-md-6">
                          <div className="form-group mb-3">
                            <label className="font-weight-600 text-dark form-label">
                              {_t(t("Sort Order"))}
                            </label>
                            <input
                              type="number"
                              name="sort_order"
                              className="form-control border-secondary-light"
                              value={formData.sort_order}
                              onChange={handleInputChange}
                              placeholder="e.g. 16"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="row mb-4 bg-light mx-0 p-3 rounded border border-light">
                        <div className="col-md-6">
                          <div className="custom-control custom-switch">
                            <input
                              type="checkbox"
                              className="custom-control-input"
                              id="is_critical_switch"
                              name="is_critical"
                              checked={formData.is_critical == 1}
                              onChange={handleInputChange}
                            />
                            <label className="custom-control-label" htmlFor="is_critical_switch" style={{ paddingTop: '2px', cursor: 'pointer' }}>
                              <h6 className="mb-0 text-dark lg-text">{_t(t("Is Critical Parameter?"))}</h6>
                              <small className="text-muted ml-1">{_t(t("Toggle if this question is critical for the audit."))}</small>
                            </label>
                          </div>
                        </div>

                        <div className="col-md-6">
                          <div className="custom-control custom-switch">
                            <input
                              type="checkbox"
                              className="custom-control-input"
                              id="is_active_switch"
                              name="is_active"
                              checked={formData.is_active == 1}
                              onChange={handleInputChange}
                            />
                            <label className="custom-control-label" htmlFor="is_active_switch" style={{ paddingTop: '2px', cursor: 'pointer' }}>
                              <h6 className="mb-0 text-dark lg-text">{_t(t("Active Status"))}</h6>
                              <small className="text-muted ml-1">{_t(t("Toggle whether this question appears in new audits."))}</small>
                            </label>
                          </div>
                        </div>
                      </div>
                      
                      <div className="d-flex justify-content-end py-2 mt-2 sticky-bottom-actions">
                        <button
                          type="button"
                          className="btn rounded hov-btn mr-3"
                          onClick={() => history.push("/dashboard/audit/rcr-question-list")}
                        >
                          <i className="fa fa-arrow-left" aria-hidden="true"></i> {_t(t("Back"))}
                        </button>
                        <button
                          type="submit"
                          className="btn btn-primary rounded"
                          disabled={submitting}
                        >
                          <i className="fa fa-floppy-o" aria-hidden="true"></i> {submitting ? _t(t("Saving...")) : _t(t("Save"))}
                        </button>
                      </div>
                      </div>
                    </form>
                    )}
                    </>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      
      <style>{`
        .custom-select, .form-control { font-size: 13px; padding: 0.4rem 0.6rem; border-radius: 0.25rem; }
        .custom-select { height: calc(2rem + 2px); }
        .border-secondary-light { border-color: #cbd5e1; }
        .form-control:focus { border-color: #0a8d6e; box-shadow: 0 0 0 0.2rem rgba(10, 141, 110, 0.15); }
        .custom-switch .custom-control-label::before { width: 34px; border-radius: 34px; height: 18px; top: 0.1rem; }
        .custom-switch .custom-control-label::after { width: 14px; height: 14px; border-radius: 50%; top: calc(0.1rem + 2px); left: calc(-2.25rem + 2px); }
        .custom-control-input:checked ~ .custom-control-label::before { background-color: #0a8d6e; border-color: #0a8d6e; }
        .custom-switch .custom-control-input:checked ~ .custom-control-label::after { transform: translateX(16px); }
        .sticky-bottom-actions { position: sticky; bottom: -1px; background: #fff; z-index: 100; margin-left: -1rem; margin-right: -1rem; margin-bottom: -1rem; padding-left: 1rem; padding-right: 1rem; border-top: 1px solid #e2e8f0; box-shadow: 0 -4px 6px -1px rgba(0, 0, 0, 0.02); border-bottom-left-radius: 0.25rem; border-bottom-right-radius: 0.25rem; }
      `}</style>
    </>
  );
};

export default RcrQuestionAction;
